import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "as", "required", "label", "labelId", "labelSize", "children", "alert", "alertType", "description", "descriptionType", "shortHint", "bold"];
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.string.bold.js";
import "core-js/modules/es.array.join.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import React, { useState } from 'react';
import styled from 'styled-components';
import Alert from 'components/Alert';
import Box from 'components/Box';
import HintTooltip from 'components/HintTooltip';
import Text from 'components/Text';
import Status from 'components/Status';
import useUniqueId from 'hooks/useUniqueId';
import { getLength } from 'utils/ui';
import { FormFieldContext } from './FormFieldContext';
import { useDefaultFormFieldLayout } from './DefaultFormFieldLayout';
var LAYOUT_PROP_NAMES = ['bold', 'horizontal', 'horizontalBreakpoint', 'horizontalLabelWidth', 'horizontalLabelOffset'];
var Layout = styled(Box).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !LAYOUT_PROP_NAMES.includes(prop);
  }
}).withConfig({
  displayName: "FormField__Layout",
  componentId: "sc-17ardxb-0"
})(["display:grid;grid-template-columns:1fr auto;grid-template-areas:'label hint' 'field field' 'desc  desc';align-items:baseline;", ""], function (p) {
  return p.horizontal && "\n\t\t@media (min-width: ".concat(p.theme.globals.breakpoints[p.horizontalBreakpoint], ") {\n\t\t\tgrid-template-columns: ").concat(getLength(p.horizontalLabelWidth), " 1fr;\n\t\t\tgrid-template-areas:\n\t\t\t\t'label field'\n\t\t\t\t'hint  field'\n\t\t\t\t'hint  desc';\n\n\t\t\t").concat(p.horizontalLabelOffset !== undefined && "\n\t\t\t\talign-items: start;\n\t\t\t", "\n\t\t}\n\t");
});
var LabelWrapper = styled(Box).withConfig({
  displayName: "FormField__LabelWrapper",
  componentId: "sc-17ardxb-1"
})(["grid-area:label;float:left;"]);
var ShortHintWrapper = styled(Box).withConfig({
  displayName: "FormField__ShortHintWrapper",
  componentId: "sc-17ardxb-2"
})(["grid-area:hint;"]);
var FieldWrapper = styled(Box).withConfig({
  displayName: "FormField__FieldWrapper",
  componentId: "sc-17ardxb-3"
})(["grid-area:field;"]);
var DescriptionWrapper = styled(Box).withConfig({
  displayName: "FormField__DescriptionWrapper",
  componentId: "sc-17ardxb-4"
})(["grid-area:desc;"]);
function FormField(_ref) {
  var id = _ref.id,
    _ref$as = _ref.as,
    as = _ref$as === void 0 ? 'label' : _ref$as,
    required = _ref.required,
    label = _ref.label,
    labelIdProp = _ref.labelId,
    labelSize = _ref.labelSize,
    children = _ref.children,
    alert = _ref.alert,
    alertType = _ref.alertType,
    description = _ref.description,
    _ref$descriptionType = _ref.descriptionType,
    descriptionType = _ref$descriptionType === void 0 ? 'default' : _ref$descriptionType,
    shortHint = _ref.shortHint,
    _ref$bold = _ref.bold,
    bold = _ref$bold === void 0 ? true : _ref$bold,
    layoutProps = _objectWithoutProperties(_ref, _excluded);
  var fieldId = useUniqueId(id);
  var labelId = labelIdProp || "".concat(fieldId, "-label");
  var descriptionId = "".concat(fieldId, "-desc");
  var shortHintId = "".concat(fieldId, "-hint");
  var alertId = "".concat(fieldId, "-alert");
  var descriptionIds = [alertId, shortHint && shortHintId, description && descriptionId].filter(Boolean).join(' ');

  // A 'custom control' is a non-native form control that can't be
  // labelled with a <label for="field-id"> element, but uses
  // the aria-labelledby="label-id" attribute instead.
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isCustomControl = _useState2[0],
    setIsCustomControl = _useState2[1];
  var htmlFor = as === 'label' && !isCustomControl ? fieldId : undefined;
  var groupElement;
  var labelWrapperElement;
  var labelElement = as;
  if (as === 'fieldset') {
    groupElement = as;
    // The below should really be a 'legend' element, but unfortunately
    // this element comes with severe styling limitations (see #14820).
    // To make up for the loss in automatic labelling that `<legend>`
    // provides, we manually label the groupElement via `aria-labelledby`.
    labelWrapperElement = 'strong';
    labelElement = 'span';
  }
  var _useDefaultFormFieldL = useDefaultFormFieldLayout(layoutProps),
    horizontal = _useDefaultFormFieldL.horizontal,
    horizontalBreakpoint = _useDefaultFormFieldL.horizontalBreakpoint,
    horizontalLabelWidth = _useDefaultFormFieldL.horizontalLabelWidth,
    horizontalLabelOffset = _useDefaultFormFieldL.horizontalLabelOffset;
  var fieldContext = {
    id: fieldId,
    labelId: labelId,
    descriptionIds: descriptionIds,
    required: required,
    horizontal: horizontal,
    setIsCustomControl: setIsCustomControl
  };
  return /*#__PURE__*/React.createElement(Layout, {
    as: groupElement,
    "aria-labelledby": as === 'fieldset' ? labelId : undefined,
    horizontal: horizontal,
    horizontalBreakpoint: horizontalBreakpoint,
    horizontalLabelWidth: horizontalLabelWidth,
    horizontalLabelOffset: horizontalLabelOffset
  }, /*#__PURE__*/React.createElement(LabelWrapper, {
    overflow: "wrap",
    breakpoints: horizontal ? [horizontalBreakpoint] : undefined,
    mt: [0, horizontalLabelOffset],
    mb: ['xxs', 0],
    mr: 'xxs',
    as: labelWrapperElement
  }, /*#__PURE__*/React.createElement(Text, {
    bold: bold,
    id: labelId,
    as: labelElement,
    htmlFor: htmlFor,
    size: labelSize
  }, label), description && descriptionType === 'tooltip' && /*#__PURE__*/React.createElement(HintTooltip, {
    content: description,
    id: descriptionId,
    spacingLeft: "xxs"
  })), shortHint && /*#__PURE__*/React.createElement(ShortHintWrapper, {
    dimmed: true,
    fontSize: "s",
    id: shortHintId
  }, shortHint), /*#__PURE__*/React.createElement(FieldWrapper, null, /*#__PURE__*/React.createElement(FormFieldContext.Provider, {
    value: fieldContext
  }, children)), /*#__PURE__*/React.createElement(DescriptionWrapper, null, /*#__PURE__*/React.createElement(Status, {
    mt: alert ? 'xxs' : undefined,
    id: alertId
  }, alert && /*#__PURE__*/React.createElement(Alert, {
    type: alertType
  }, alert)), description && descriptionType === 'default' && /*#__PURE__*/React.createElement(Text, {
    as: "p",
    id: descriptionId,
    dimmed: true,
    size: "s",
    overflow: "wrap",
    mt: "xxs"
  }, description)));
}
export { FormFieldContext, useFormFieldContext, useCustomControl } from './FormFieldContext';
export { DefaultFormFieldLayout } from './DefaultFormFieldLayout';
export { FormFieldAlignedContent } from './FormFieldAlignedContent';
export default FormField;